/**
 * CommitGateDialog — "Commit Act I & Begin Drafting"
 *
 * Shown when the writer clicks "Draft" in the WorkflowRibbon for
 * the first time on a project. Displays an Act I readiness checklist,
 * then lets them either complete missing items or lock and proceed.
 *
 * On commit the project's Act I is flagged as locked in localStorage
 * and the writer is dropped straight into the Manuscript editor.
 */
import React, { useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lightbulb, Users, Globe, Lock, AlertTriangle, PenLine, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';

/* ── helpers ─────────────────────────────────────────────────────── */

export function getAct1CommittedKey(projectId) {
  return `cr_act1_committed_${projectId}`;
}

export function isAct1Committed(projectId) {
  if (!projectId) return false;
  try { return !!localStorage.getItem(getAct1CommittedKey(projectId)); } catch { return false; }
}

export function commitAct1(projectId) {
  if (!projectId) return;
  try { localStorage.setItem(getAct1CommittedKey(projectId), Date.now().toString()); } catch { /* ok */ }
}

/* ── Checklist item ──────────────────────────────────────────────── */

function ChecklistRow({ icon: Icon, label, done, desc, onFix }) {
  return (
    <div className={cn(
      'flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
      done ? 'border-emerald-500/25 bg-emerald-500/5' : 'border-amber-500/25 bg-amber-500/5',
    )}>
      <span className={cn('mt-0.5 shrink-0', done ? 'text-emerald-500' : 'text-amber-500')}>
        {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
      </span>
      <div className="flex flex-1 flex-col gap-0.5">
        <span className={cn('text-[13px] font-semibold', done ? 'text-foreground' : 'text-amber-200')}>{label}</span>
        <span className="text-[11px] text-muted-foreground leading-snug">{desc}</span>
      </div>
      {!done && onFix && (
        <button
          type="button"
          onClick={onFix}
          className="shrink-0 rounded-md border border-amber-500/30 px-2.5 py-1 text-[11px] font-medium text-amber-400 hover:bg-amber-500/10 transition-colors"
        >
          Open →
        </button>
      )}
      {done && (
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-emerald-500/70">Done</span>
      )}
    </div>
  );
}

/* ── Main dialog ──────────────────────────────────────────────────── */

export function CommitGateDialog({ open, onOpenChange, onCommitAndDraft }) {
  const { project, characters = [], variables = {} } = usePW();

  const checks = useMemo(() => {
    if (!project) return [];
    const hasIdea = !!(variables?.genre || variables?.tone || project?.genre || project?.logline);
    const hasCharacters = characters.length > 0;
    const hasWorldBible = !!(hasCharacters || hasIdea || variables?.themes || variables?.setting);

    return [
      {
        id: 'idea',
        icon: Lightbulb,
        label: 'Story Premise',
        desc: 'Genre, tone, and logline are set in the Variables panel.',
        done: hasIdea,
        onFix: () => {
          onOpenChange(false);
          window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'variables' }));
        },
      },
      {
        id: 'characters',
        icon: Users,
        label: 'Cast of Characters',
        desc: 'At least one character is defined in the Character Bible.',
        done: hasCharacters,
        onFix: () => {
          onOpenChange(false);
          window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'bible' }));
        },
      },
      {
        id: 'worldBible',
        icon: Globe,
        label: 'World Bible',
        desc: 'Setting, themes, and world-building are roughed in.',
        done: hasWorldBible,
        onFix: () => {
          onOpenChange(false);
          window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'variables' }));
        },
      },
    ];
  }, [project, characters, variables, onOpenChange]);

  const allDone = checks.every((c) => c.done);
  const doneCount = checks.filter((c) => c.done).length;

  const handleCommit = () => {
    if (project?.id) commitAct1(project.id);
    onOpenChange(false);
    onCommitAndDraft?.();
  };

  const handleProceedAnyway = () => {
    if (project?.id) commitAct1(project.id);
    onOpenChange(false);
    onCommitAndDraft?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px] bg-[hsl(var(--chrome))] border-chrome-border"
        data-testid="commit-gate-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Lock className="h-3.5 w-3.5" />
            </span>
            <DialogTitle className="text-[16px] font-bold">Commit Act I & Begin Drafting</DialogTitle>
          </div>
          <DialogDescription className="text-[12.5px] leading-relaxed text-muted-foreground">
            Completing your foundation before drafting keeps your story consistent.
            You can still edit Act I later, but changes will trigger a <strong className="text-foreground">Drift Alert</strong>.
          </DialogDescription>
        </DialogHeader>

        {/* Progress pill */}
        <div className="flex items-center gap-2 rounded-md bg-muted/30 px-3 py-2 my-1">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                allDone ? 'bg-emerald-500' : 'bg-primary',
              )}
              style={{ width: `${(doneCount / checks.length) * 100}%` }}
            />
          </div>
          <span className="text-[11px] font-semibold text-muted-foreground shrink-0">
            {doneCount}/{checks.length} complete
          </span>
        </div>

        {/* Checklist */}
        <div className="flex flex-col gap-2 my-1">
          {checks.map((check) => (
            <ChecklistRow key={check.id} {...check} />
          ))}
        </div>

        {!allDone && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11.5px] text-amber-400/80">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Incomplete items may cause continuity issues during drafting. You can still proceed.</span>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-1">
          {allDone ? (
            <Button
              onClick={handleCommit}
              data-testid="commit-gate-confirm-btn"
              className="flex-1 gap-2 bg-primary hover:bg-primary/90"
            >
              <PenLine className="h-3.5 w-3.5" />
              Commit Act I & Begin Drafting
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleProceedAnyway}
                data-testid="commit-gate-proceed-anyway-btn"
                className="flex-1 border-border/60 text-muted-foreground hover:text-foreground"
              >
                Proceed to Draft anyway
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                data-testid="commit-gate-finish-act1-btn"
                className="flex-1 gap-2"
              >
                Finish Act I first
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
